import type { ComponentType } from 'react'
import type { WidgetKind } from '@racedash/shared'
import type { WidgetEditorProps, WidgetProps } from './props'
import CarNumber from './CarNumber'
import Clock, { Editor as ClockEditor } from './Clock'
import LapList, { Editor as LapListEditor } from './LapList'
import LapNumber from './LapNumber'
import LapTime, { Editor as LapTimeEditor } from './LapTime'
import Messages, { Editor as MessagesEditor } from './Messages'
import Position, { Editor as PositionEditor } from './Position'
import Sectors from './Sectors'
import StopBy from './StopBy'
import PitWindow from './PitWindow'
import Driver from './Driver'
import Stint from './Stint'
import Fuel, { Editor as FuelEditor } from './Fuel'
import FuelStrategy from './FuelStrategy'
import Gaps from './Gaps'
import DriverBudget from './DriverBudget'
import SinceLastLap from './SinceLastLap'
import TrackStatus from './TrackStatus'

/**
 * Kind to view.
 *
 * The mapped type is the whole trick, twice over: a widget added to the union
 * in @racedash/shared and not given a view here does not compile, and a view
 * wired to the wrong kind does not compile either, because each entry's props
 * are narrowed to that kind's own config.
 *
 * Adding a widget is therefore one file plus one line, and forgetting the line
 * is caught by the compiler rather than at three in the morning.
 */
export const widgetViews: { [K in WidgetKind]: ComponentType<WidgetProps<K>> } = {
  carNumber: CarNumber,
  position: Position,
  lapNumber: LapNumber,
  lapTime: LapTime,
  sectors: Sectors,
  stopBy: StopBy,
  pitWindow: PitWindow,
  driver: Driver,
  stint: Stint,
  fuel: Fuel,
  fuelStrategy: FuelStrategy,
  driverBudget: DriverBudget,
  sinceLastLap: SinceLastLap,
  gaps: Gaps,
  lapList: LapList,
  messages: Messages,
  trackStatus: TrackStatus,
  clock: Clock,
}

/**
 * Kind to settings form, where the kind has any settings.
 *
 * `null` rather than a missing key, and the mapped type makes it mandatory —
 * so "this widget has nothing to configure" is something a widget author has to
 * say, not something they can forget. Forgetting would silently produce a
 * settings dialog with nothing in it.
 */
export const widgetEditors: {
  [K in WidgetKind]: ComponentType<WidgetEditorProps<K>> | null
} = {
  carNumber: null,
  position: PositionEditor,
  lapNumber: null,
  lapTime: LapTimeEditor,
  sectors: null,
  stopBy: null,
  pitWindow: null,
  driver: null,
  stint: null,
  fuel: FuelEditor,
  fuelStrategy: null,
  driverBudget: null,
  sinceLastLap: null,
  gaps: null,
  lapList: LapListEditor,
  messages: MessagesEditor,
  trackStatus: null,
  clock: ClockEditor,
}
